

/**
 * Validates a dollar fraction input change character-by-character.
 * Extracted from DynamicCellEditorQuoteDollarFraction.handleInput so both
 * the fill price editor and the select panel level input can reuse it.
 *
 * @param input - The new input value to validate
 * @returns true if the input should be accepted, false to block
 */
export function validateDollarFractionInput(input: string): boolean {
  // Allow only valid characters (`0-9`, `.`, `-`, `+`)
  if (!/^[\d.\-+]*$/.test(input)) {
    return false;
  }

  // Prevent multiple `.` (only one allowed)
  if ((input.match(/\./g) || []).length > 1) {
    return false;
  }

  // Prevent multiple `-` (two MAX allowed if 1 is used for a negative value.)
  if ((input.substring(1).match(/-/g) || []).length > 1) {
    return false;
  }

  // Prevent `.` and `-` from coexisting
  if (input.includes(".") && input.includes("-")) {
    return false;
  }

  // Prevent multiple `+` (only one allowed, must be at the end)
  if (
    (input.match(/\+/g) || []).length > 1 ||
    (input.includes("+") && !input.endsWith("+"))
  ) {
    return false;
  }

  // Prevent `+` if there is no `-` before it
  if (input.includes("+") && !input.includes("-")) {
    return false;
  }

  // Ensure at least one digit exists between `-` and `+`
  if (input.includes("-") && input.includes("+")) {
    const match = input.match(/-(\d{2,})\+/);
    if (!match || match[1].length === 0) {
      return false;
    }
  }

  // The two numbers after the hyphen cannot be > 31
  const dashMatch = input.substring(1).match(/-(\d*)/);

  if (dashMatch && dashMatch[1].length == 1) {
    if (Number(dashMatch[1]) > 3) {
      return false;
    }
  }

  if (dashMatch && dashMatch[1].length == 2) {
    if (Number(dashMatch[1]) > 31) {
      return false;
    }
  }

  // Z-value (3rd digit) tick size rules - using ruleOfEights
  if (dashMatch && dashMatch[1].length > 2) {
    if (
      dashMatch[1][2] == "0" ||
      dashMatch[1][2] == "8" ||
      dashMatch[1][2] == "9"
    ) {
      return false;
    }
  }

  // Max 3 digits after `-`
  if (dashMatch && dashMatch[1].length > 3) {
    return false;
  }

  return true;
}
